import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import SkeletonPiece, { SkeletonPieceColor } from 'src/components/atoms/SkeletonPiece';
import { StyledButtonComponent, StyledComponent, StyledInputComponent } from './styles';

type InputTextOnChangeProps = {
  field: string;
  value: string;
};

export type InputSearchProps = {
  name: string;
  value: string;
  placeholder: string;
  onSearch: (e: InputTextOnChangeProps) => void;
  onChange?: (e: InputTextOnChangeProps) => void;
  onClear?: () => void;
  isDisabled?: boolean;
  className?: string;
  isLoading?: boolean;
};

const InputSearch: React.FC<InputSearchProps> = (props) => {
  const {
    onSearch,
    onClear: onClearing,
    isDisabled,
    name,
    value,
    placeholder,
    className,
    isLoading
  } = props;

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      [name]: value
    }
  });

  // `defaultValues` is only read when the form is created, so later changes to
  // `value` have to be pushed in.
  useEffect(() => {
    methods.setValue(name, value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, name]);

  const onSubmit = (data: any) => {
    const result = Object.keys(data).map((e) => ({ field: e, value: data[e]?.trim() }));
    onSearch(result[0]);
  };

  if (isLoading) {
    return (
      <SkeletonPiece
        width={220}
        height={40}
        color={SkeletonPieceColor.gray50}
        className="noPadding"
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <StyledComponent className={className}>
          <StyledInputComponent>
            <InputText
              name={name}
              style={{ marginBottom: 0 }}
              placeholder={placeholder}
              value={value}
              isDisabled={isDisabled}
              type={InputTextType.text}
              showLabel={false}
              showOptionalLabel={false}
              endAndorment={
                value && (
                  <Button
                    onClick={() => {
                      methods.setValue(name, '');
                      onClearing && onClearing();
                    }}
                    className="end-andorment"
                    variant={ButtonVariant.ghost}
                    name={`${name}__close_button`}
                    type={ButtonType.button}
                    isDisabled={isDisabled}
                  >
                    <Icon icon="icon-close" />
                  </Button>
                )
              }
            />
          </StyledInputComponent>
          <StyledButtonComponent>
            <Button
              name={`${name}__search_button`}
              color={ButtonColor.primary}
              variant={ButtonVariant.contained}
              size={ButtonSize.medium}
              isLoading={false}
              isDisabled={isDisabled}
              type={ButtonType.submit}
              onClick={() => methods.handleSubmit(onSubmit)()}
            >
              <Icon icon="icon-search" />
            </Button>
          </StyledButtonComponent>
        </StyledComponent>
      </form>
    </FormProvider>
  );
};

InputSearch.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  isLoading: PropTypes.bool
};

InputSearch.defaultProps = {
  name: 'search',
  value: '',
  placeholder: 'Search'
};

export default InputSearch;
